//src/pages/ReportsPage.tsx
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import ReactECharts from "echarts-for-react";
import { BarChart3, CalendarDays, Check, ChevronDown, Download, FileText, Loader2, TrendingUp } from "lucide-react";
import { toast } from "sonner";

import {
  useDownloadRiskInventoryExcelReport,
  useGenerateCampaignAnalyticsReport,
  useGeneratePsychosocialEvolutionReport,
  useGenerateRiskInventoryActionPlansReport,
  useReportsOptions,
} from "@/hooks/useReports";
import type {
  CampaignAnalyticsReport,
  CampaignQuestionItem,
  DistributionBucket,
  PsychosocialEvolutionReport,
  ReportOption,
  ReportResult,
  RiskInventoryActionPlansReport,
  SeriesValue,
} from "@/types/reports";

type ReportType = "evolution" | "campaign" | "inventory" | "inventory_export";

type ReportFilters = {
  inventory_id: string;
  campaign_id: string;
  dimension_id: string;
  risk_id: string;
  unit_id: string;
  department_id: string;
  job_position_id: string;
  worker_group_id: string;
  period_start: string;
  period_end: string;
  risk_classification: string;
  item_status: string;
  action_status: string;
  deadline_start: string;
  deadline_end: string;
};

const emptyFilters: ReportFilters = {
  inventory_id: "",
  campaign_id: "",
  dimension_id: "",
  risk_id: "",
  unit_id: "",
  department_id: "",
  job_position_id: "",
  worker_group_id: "",
  period_start: "",
  period_end: "",
  risk_classification: "",
  item_status: "",
  action_status: "",
  deadline_start: "",
  deadline_end: "",
};

const reportCards: Array<{ id: ReportType; title: string; description: string; icon: typeof BarChart3 }> = [
  {
    id: "evolution",
    title: "Evolução Psicossocial por Inventário",
    description: "Compara campanhas do mesmo inventário e mostra tendências de dimensões, riscos e ações ao longo do tempo.",
    icon: TrendingUp,
  },
  {
    id: "campaign",
    title: "Análise por Campanha",
    description: "Mostra diagnóstico da campanha por dimensão e pergunta, com médias e distribuição percentual das respostas.",
    icon: BarChart3,
  },
  {
    id: "inventory",
    title: "Riscos, Inventário e Planos de Ação",
    description: "Consolida riscos atuais, planos vinculados, ações pendentes, responsáveis, prazos e pendências.",
    icon: FileText,
  },
  {
    id: "inventory_export",
    title: "Exportar Inventário em Excel",
    description: "Gera a planilha tabular do inventário para importação em outros sistemas e uso externo.",
    icon: Download,
  },
];

function selectLabel(item: ReportOption) {
  return item.title || item.name || item.code || item.id;
}

function formatDateTime(value?: string | null) {
  if (!value) return "-";
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function formatDateOnly(value?: string | null) {
  if (!value) return null;
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function formatNumber(value?: number | string | null, digits = 2) {
  if (value === undefined || value === null || value === "") return "-";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(n);
}

function normalizeParam(value: string) {
  return value || undefined;
}

function saveDownload(blob: Blob, filename?: string) {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename || "inventario-riscos.xlsx";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
}

function statusLabel(status?: string | null) {
  const map: Record<string, string> = {
    pending: "A Fazer",
    in_progress: "Em andamento",
    completed: "Concluído",
    cancelled: "Cancelado",
    draft: "Rascunho",
    active: "Ativo",
    inactive: "Inativo",
    archived: "Arquivado",
    closed: "Encerrada",
    open: "Publicada",
  };
  return map[status || ""] || status || "-";
}

function trendClass(trend?: string | null) {
  if (trend === "improved") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (trend === "worsened") return "border-red-200 bg-red-50 text-red-700";
  if (trend === "oscillated") return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-slate-200 bg-slate-50 text-slate-700";
}

function riskLevelClass(level?: string | null) {
  if (level === "critical") return "border-red-200 bg-red-50 text-red-700";
  if (level === "high") return "border-amber-200 bg-amber-50 text-amber-700";
  if (level === "moderate") return "border-slate-200 bg-slate-50 text-slate-700";
  if (level === "low") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (level === "not_identified") return "border-slate-200 bg-slate-100 text-slate-600";
  return "border-slate-200 bg-slate-50 text-slate-700";
}

const chartPalette = ["#2563EB", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4", "#84CC16", "#F97316"];
const chartSymbols = ["circle", "rect", "roundRect", "triangle", "diamond", "pin", "arrow", "emptyCircle"];

function formatVariation(value?: number | string | null) {
  if (value === undefined || value === null || value === "") return "-";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  const formatted = formatNumber(Math.abs(n));
  if (n > 0) return "+" + formatted;
  if (n < 0) return "-" + formatted;
  return formatted;
}

function campaignLabel(campaign: { period_label?: string | null; title?: string | null; opens_at?: string | null }) {
  return formatDateOnly(campaign.opens_at) || campaign.period_label || campaign.title || "Campanha";
}

function valueLabel(value?: number | null) {
  return value === null || value === undefined ? "-" : formatNumber(value);
}

function riskPointLabel(point?: SeriesValue | null) {
  if (!point || point.value === null || point.value === undefined || Number(point.value) === 0) {
    return "0,00 Risco não identificado";
  }
  return point.risk_level_name ? formatNumber(point.value) + " " + point.risk_level_name : formatNumber(point.value);
}

function riskScoreLabel(point?: SeriesValue | null) {
  if (!point || point.value === null || point.value === undefined) return "0,00";
  return formatNumber(point.value);
}

function riskCellDescription(point?: SeriesValue | null) {
  if (!point || point.value === null || point.value === undefined || Number(point.value) === 0) return "Risco não identificado nesta campanha";

  const parts = [];
  if (point.risk_level_name) parts.push(point.risk_level_name);
  if (point.risk_average_score !== null && point.risk_average_score !== undefined) parts.push(`média ${formatNumber(point.risk_average_score)}`);
  if (point.dimensions_count !== null && point.dimensions_count !== undefined) parts.push(`${point.dimensions_count} dim.`);
  return parts.length ? parts.join(" • ") : point.dimension_scores_text || "-";
}

function riskDimensionsSummary(item: EvolutionChartItem) {
  const names = new Set<string>();

  if (item.dimension_names) {
    item.dimension_names.split(";").map((value) => value.trim()).filter(Boolean).forEach((value) => names.add(value));
  }

  item.campaign_values.forEach((point) => {
    point.dimension_names?.split(";").map((value) => value.trim()).filter(Boolean).forEach((value) => names.add(value));
  });

  return Array.from(names).join("; ") || "Sem dimensões associadas";
}

type EvolutionChartItem = {
  dimension_name?: string | null;
  dimension_names?: string | null;
  risk_name?: string | null;
  trend?: string | null;
  campaign_values: SeriesValue[];
};

function EvolutionLineChart({
  items,
  campaigns,
  mode,
}: {
  items: EvolutionChartItem[];
  campaigns: PsychosocialEvolutionReport["campaigns"];
  mode: "dimension" | "risk";
}) {
  const xLabels = campaigns.map(campaignLabel);
  const legendData = items.map((item) => mode === "risk" ? item.risk_name || "Sem nome" : item.dimension_name || "Sem nome");

  const option = useMemo(() => ({
    color: chartPalette,
    animation: true,
    grid: { top: 88, right: 36, bottom: 48, left: 56, containLabel: true },
    legend: {
      type: "scroll",
      top: 8,
      left: 8,
      right: 8,
      itemWidth: 12,
      itemHeight: 8,
      textStyle: { color: "#475569", fontSize: 11 },
      data: legendData,
    },
    tooltip: {
      trigger: "axis",
      triggerOn: "mousemove|click",
      axisPointer: { type: "line", lineStyle: { color: "#94A3B8", width: 1, type: "dashed" } },
      backgroundColor: "rgba(15,23,42,0.96)",
      borderWidth: 0,
      padding: 12,
      textStyle: { color: "#FFFFFF", fontSize: 12 },
      formatter: (params: Array<{ seriesName: string; dataIndex: number; marker: string; value?: number | null }>) => {
        const title = xLabels[params?.[0]?.dataIndex ?? 0] || "Campanha";
        const rows = params
          .filter((param) => mode === "risk" || param.value !== null && param.value !== undefined)
          .map((param) => {
            const item = items.find((candidate) => (mode === "risk" ? candidate.risk_name : candidate.dimension_name) === param.seriesName);
            const point = item?.campaign_values.find((value) => value.campaign_id === campaigns[param.dataIndex]?.id);
            if (mode === "risk") {
              return `${param.marker}<strong>${param.seriesName}</strong>: ${riskPointLabel(point)}<br/><span style="opacity:.78">${riskCellDescription(point)}</span>`;
            }
            return `${param.marker}<strong>${param.seriesName}</strong>: ${valueLabel(param.value ?? null)}`;
          });
        return [`<strong>${title}</strong>`, ...rows].join("<br/>");
      },
    },
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: xLabels,
      axisLabel: { color: "#64748B" },
      axisLine: { lineStyle: { color: "#CBD5E1" } },
      axisTick: { show: false },
    },
    yAxis: {
      type: "value",
      min: mode === "dimension" ? 1 : 0,
      max: mode === "dimension" ? 5 : undefined,
      name: mode === "dimension" ? "Média" : "Score",
      nameTextStyle: { color: "#64748B", fontSize: 11, padding: [0, 0, 6, 0] },
      axisLabel: { color: "#64748B" },
      splitLine: { lineStyle: { color: "#E2E8F0", type: "dashed" } },
    },
    series: items.map((item, index) => ({
      name: mode === "risk" ? item.risk_name || "Sem nome" : item.dimension_name || "Sem nome",
      type: "line",
      smooth: true,
      connectNulls: mode === "risk",
      showSymbol: true,
      symbol: mode === "risk" ? chartSymbols[index % chartSymbols.length] : "circle",
      symbolSize: mode === "risk" ? 10 : 8,
      lineStyle: { width: 3 },
      itemStyle: { borderColor: "#FFFFFF", borderWidth: mode === "risk" ? 2 : 1 },
      emphasis: { focus: "series", scale: true, lineStyle: { width: 5 } },
      label: {
        show: true,
        position: "top",
        fontSize: 10,
        color: "#334155",
        formatter: (params: { value?: number | null }) => mode === "risk" && Number(params.value || 0) === 0 ? "0" : valueLabel(params.value),
      },
      areaStyle: { opacity: mode === "risk" ? 0.025 : 0.04 },
      data: campaigns.map((campaign) => {
        const point = item.campaign_values.find((value) => value.campaign_id === campaign.id);
        if (mode === "risk") return point?.value === null || point?.value === undefined ? 0 : Number(point.value);
        return point?.value === null || point?.value === undefined ? null : Number(point.value);
      }),
    })),
  }), [campaigns, items, legendData, mode, xLabels]);

  return (
    <div className="report-chart-frame rounded-2xl border border-slate-100 bg-slate-50/70 p-3 print:bg-white">
      <div className="report-screen-chart">
        <ReactECharts option={option} className="report-chart" style={{ height: 380, width: "100%" }} opts={{ renderer: "svg" }} notMerge lazyUpdate />
      </div>
      <PrintEvolutionLineChart items={items} campaigns={campaigns} mode={mode} />
    </div>
  );
}

function PrintEvolutionLineChart({
  items,
  campaigns,
  mode,
}: {
  items: EvolutionChartItem[];
  campaigns: PsychosocialEvolutionReport["campaigns"];
  mode: "dimension" | "risk";
}) {
  const xLabels = campaigns.map(campaignLabel);
  const rawValues = items
    .flatMap((item) =>
      campaigns.map((campaign) => {
        const point = item.campaign_values.find((value) => value.campaign_id === campaign.id);
        if (mode === "risk") return Number(point?.value ?? 0);
        if (point?.value === null || point?.value === undefined) return null;
        return Number(point.value);
      }),
    )
    .filter((value): value is number => value !== null && Number.isFinite(value));

  const minValue = mode === "dimension" ? 1 : 0;
  const maxValue = mode === "dimension" ? 5 : Math.max(15, Math.ceil(Math.max(...rawValues, 0) / 3) * 3);
  const ticks = mode === "dimension"
    ? [1, 2, 3, 4, 5]
    : Array.from({ length: 6 }, (_, index) => (maxValue / 5) * index);

  const width = 1000;
  const height = 260;
  const plotLeft = 64;
  const plotRight = 24;
  const plotTop = 78;
  const plotBottom = 214;
  const plotWidth = width - plotLeft - plotRight;
  const plotHeight = plotBottom - plotTop;
  const range = Math.max(maxValue - minValue, 1);

  const xFor = (index: number) => campaigns.length <= 1 ? plotLeft + plotWidth / 2 : plotLeft + (index / (campaigns.length - 1)) * plotWidth;
  const yFor = (value: number) => plotBottom - ((value - minValue) / range) * plotHeight;

  return (
    <div className="report-print-chart">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={mode === "dimension" ? "Gráfico de evolução por dimensão" : "Gráfico de evolução por risco"}>
        {items.map((item, index) => {
          const label = mode === "risk" ? item.risk_name || "Sem nome" : item.dimension_name || "Sem nome";
          const color = chartPalette[index % chartPalette.length];
          const legendX = 24 + (index % 3) * 310;
          const legendY = 20 + Math.floor(index / 3) * 18;
          return (
            <g key={label}>
              <line x1={legendX} y1={legendY} x2={legendX + 14} y2={legendY} stroke={color} strokeWidth="3" />
              <circle cx={legendX + 7} cy={legendY} r="3" fill={color} />
              <text x={legendX + 20} y={legendY + 4} fontSize="10" fill="#475569">{label}</text>
            </g>
          );
        })}

        <text x={plotLeft} y={plotTop - 14} fontSize="11" fill="#64748B">{mode === "dimension" ? "Média" : "Score"}</text>

        {ticks.map((tick) => {
          const y = yFor(tick);
          return (
            <g key={tick}>
              <line x1={plotLeft} y1={y} x2={width - plotRight} y2={y} stroke="#E2E8F0" strokeDasharray="4 4" />
              <text x={plotLeft - 12} y={y + 4} textAnchor="end" fontSize="10" fill="#64748B">{formatNumber(tick, tick % 1 === 0 ? 0 : 1)}</text>
            </g>
          );
        })}

        <line x1={plotLeft} y1={plotBottom} x2={width - plotRight} y2={plotBottom} stroke="#CBD5E1" />

        {items.map((item, itemIndex) => {
          const color = chartPalette[itemIndex % chartPalette.length];
          const points = campaigns.map((campaign, campaignIndex) => {
            const point = item.campaign_values.find((value) => value.campaign_id === campaign.id);
            if (mode === "risk") return { x: xFor(campaignIndex), y: yFor(Number(point?.value ?? 0)), value: Number(point?.value ?? 0) };
            if (point?.value === null || point?.value === undefined) return null;
            return { x: xFor(campaignIndex), y: yFor(Number(point.value)), value: Number(point.value) };
          });
          const linePoints = points.filter(Boolean).map((point) => `${point!.x},${point!.y}`).join(" ");

          return (
            <g key={mode === "risk" ? item.risk_name || itemIndex : item.dimension_name || itemIndex}>
              {linePoints && <polyline points={linePoints} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />}
              {points.map((point, pointIndex) => {
                if (!point) return null;
                return (
                  <g key={campaigns[pointIndex]?.id || pointIndex}>
                    <circle cx={point.x} cy={point.y} r="4" fill={color} stroke="#FFFFFF" strokeWidth="1.5" />
                    <text x={point.x} y={Math.max(point.y - 8, plotTop + 8)} textAnchor="middle" fontSize="9" fontWeight="600" fill="#334155">
                      {formatNumber(point.value)}
                    </text>
                  </g>
                );
              })}
            </g>
          );
        })}

        {xLabels.map((label, index) => (
          <text key={label + index} x={xFor(index)} y={plotBottom + 18} textAnchor="middle" fontSize="10" fill="#64748B">
            {label}
          </text>
        ))}
      </svg>
    </div>
  );
}

function EvolutionMatrixTable({
  items,
  campaigns,
  mode,
}: {
  items: Array<(EvolutionChartItem & { variation?: number | null; trend_label?: string | null; trend?: string | null; dimension_name?: string | null })>;
  campaigns: PsychosocialEvolutionReport["campaigns"];
  mode: "dimension" | "risk";
}) {
  return (
    <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-100">
      <table className="min-w-full text-left text-sm">
        <thead className="bg-slate-50 text-xs uppercase text-slate-500">
          <tr>
            <th className="px-3 py-2">{mode === "dimension" ? "Dimensão" : "Risco"}</th>
            {mode === "risk" && <th className="px-3 py-2">Dimensões associadas</th>}
            {campaigns.map((campaign) => (
              <th key={campaign.id} className="whitespace-nowrap px-3 py-2 text-right">{campaignLabel(campaign)}</th>
            ))}
            <th className="px-3 py-2 text-right">Variação</th>
            <th className="px-3 py-2">Tendência</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.dimension_name || item.risk_name || Math.random()} className="border-t align-top">
              <td className="px-3 py-2 font-medium text-slate-900">{mode === "dimension" ? item.dimension_name : item.risk_name}</td>
              {mode === "risk" && <td className="max-w-[280px] px-3 py-2 text-xs leading-relaxed text-slate-600">{riskDimensionsSummary(item)}</td>}
              {campaigns.map((campaign) => {
                const point = item.campaign_values.find((value) => value.campaign_id === campaign.id);
                return (
                  <td key={campaign.id} className="px-3 py-2 text-right text-slate-700">
                    {mode === "risk" ? (
                      <span
                        title={`${riskPointLabel(point)} • ${riskCellDescription(point)}`}
                        className={"inline-flex min-w-[64px] justify-center whitespace-nowrap rounded-full border px-2 py-1 text-xs font-semibold " + riskLevelClass(point?.risk_level_code)}
                      >
                        {riskScoreLabel(point)}
                      </span>
                    ) : (
                      valueLabel(point?.value ?? null)
                    )}
                  </td>
                );
              })}
              <td className="px-3 py-2 text-right font-medium">{formatVariation(item.variation)}</td>
              <td className="px-3 py-2">
                <span className={"inline-flex rounded-full border px-2 py-1 text-xs font-medium " + trendClass(item.trend)}>
                  {item.trend_label || "-"}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatCard({ label, value, helper }: { label: string; value: ReactNode; helper?: string }) {
  return (
    <div className="rounded-2xl border bg-white p-4 shadow-sm">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <div className="mt-2 text-2xl font-bold text-slate-900">{value}</div>
      {helper && <p className="mt-1 text-xs text-slate-500">{helper}</p>}
    </div>
  );
}

type SelectOption = ReportOption | { id: string; title?: string | null; name?: string | null; code?: string | null };

function SelectField({
  label,
  value,
  onChange,
  options,
  placeholder = "Todos",
  required,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  required?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const selectedOption = options.find((item) => item.id === value);
  const selectedLabel = selectedOption ? selectLabel(selectedOption as ReportOption) : placeholder;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function selectValue(nextValue: string) {
    onChange(nextValue);
    setIsOpen(false);
  }

  return (
    <div ref={wrapperRef} className="relative space-y-1.5">
      <span className="text-sm font-semibold text-slate-700">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </span>

      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        className="group flex h-11 w-full items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-3 text-left text-sm text-slate-800 shadow-sm outline-none transition hover:border-cyan-300 hover:bg-slate-50 focus:border-cyan-500 focus:ring-4 focus:ring-cyan-100"
      >
        <span className={selectedOption ? "truncate" : "truncate text-slate-400"}>{selectedLabel}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition group-hover:text-cyan-600 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute z-40 mt-2 max-h-64 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-xl ring-1 ring-slate-900/5">
          <button
            type="button"
            onClick={() => selectValue("")}
            className={`flex min-h-10 w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition ${
              !value ? "bg-cyan-500 font-medium text-white" : "text-slate-700 hover:bg-slate-50"
            }`}
          >
            <span className="truncate">{placeholder}</span>
            {!value && <Check className="h-4 w-4 shrink-0" />}
          </button>

          {options.map((item) => {
            const isSelected = item.id === value;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => selectValue(item.id)}
                className={`flex min-h-10 w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition ${
                  isSelected ? "bg-cyan-500 font-medium text-white" : "text-slate-700 hover:bg-slate-50"
                }`}
              >
                <span className="truncate">{selectLabel(item as ReportOption)}</span>
                {isSelected && <Check className="h-4 w-4 shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TextInput({
  label,
  type = "text",
  value,
  onChange,
}: {
  label: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const isDate = type === "date";

  return (
    <label className="space-y-1.5">
      <span className="text-sm font-semibold text-slate-700">{label}</span>
      <div className="relative">
        <input
          type={type}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className={`h-11 w-full rounded-xl border border-slate-200 bg-white text-sm text-slate-800 shadow-sm outline-none transition placeholder:text-slate-400 hover:border-cyan-300 hover:bg-slate-50 focus:border-cyan-500 focus:ring-4 focus:ring-cyan-100 ${
            isDate ? "px-3 pr-10 [color-scheme:light]" : "px-3"
          }`}
        />
        {isDate && <CalendarDays className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />}
      </div>
    </label>
  );
}

function ProcessingOverlay({ visible }: { visible: boolean }) {
  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 backdrop-blur-sm">
      <div className="mx-4 max-w-md rounded-2xl bg-white p-6 text-center shadow-2xl">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-50">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
        </div>
        <h2 className="mt-4 text-lg font-semibold text-slate-900">Gerando relatório</h2>
        <p className="mt-2 text-sm text-slate-600">
          Aguarde enquanto a Normex processa os dados. O botão fica bloqueado para evitar múltiplas solicitações.
        </p>
      </div>
    </div>
  );
}

function DistributionBar({ distribution }: { distribution?: DistributionBucket[] }) {
  if (!distribution?.length) return <span className="text-xs text-slate-400">Sem distribuição</span>;

  const colors = ["#10B981", "#84CC16", "#FACC15", "#FB923C", "#EF4444"];

  return (
    <div className="space-y-1">
      <div className="print-color-exact flex h-4 overflow-hidden rounded-full bg-slate-100">
        {distribution.map((bucket, index) => (
          <div
            key={bucket.score}
            title={`${bucket.score}: ${formatNumber(bucket.percent)}%`}
            style={{
              width: `${bucket.percent}%`,
              backgroundColor: colors[index] || "#CBD5E1",
            }}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-500">
        {distribution.map((bucket) => (
          <span key={bucket.score}>
            {bucket.score}: {formatNumber(bucket.percent)}%
          </span>
        ))}
      </div>
    </div>
  );
}

function MiniLine({ values }: { values: SeriesValue[] }) {
  const numeric = values.map((item) => (item.value === null || item.value === undefined ? null : Number(item.value)));
  const clean = numeric.filter((item): item is number => item !== null && Number.isFinite(item));

  if (clean.length < 2) {
    return <span className="text-xs text-slate-400">Sem série</span>;
  }

  const min = Math.min(...clean);
  const max = Math.max(...clean);
  const range = max - min || 1;
  const points = numeric
    .map((value, index) => {
      if (value === null) return null;
      const x = values.length <= 1 ? 0 : (index / (values.length - 1)) * 100;
      const y = 32 - ((value - min) / range) * 28;
      return `${x},${y}`;
    })
    .filter(Boolean)
    .join(" ");

  return (
    <svg viewBox="0 0 100 36" className="h-10 w-28">
      <polyline points={points} fill="none" stroke="currentColor" strokeWidth="3" className="text-blue-600" />
      {numeric.map((value, index) => {
        if (value === null) return null;
        const x = values.length <= 1 ? 0 : (index / (values.length - 1)) * 100;
        const y = 32 - ((value - min) / range) * 28;
        return <circle key={index} cx={x} cy={y} r="3" className="fill-blue-600" />;
      })}
    </svg>
  );
}

function ReportHeader({ report }: { report: ReportResult }) {
  return (
    <div className="border-b pb-5">
      <div className="flex flex-col justify-between gap-3 md:flex-row md:items-start">
        <div>
          <p className="text-sm font-semibold text-blue-600">Normex</p>
          <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-950">{report.metadata.report_name}</h2>
          <p className="mt-1 text-sm text-slate-600">
            {report.metadata.company_name}
            {report.metadata.cnpj ? ` • CNPJ ${report.metadata.cnpj}` : ""}
          </p>
        </div>
        <div className="text-sm text-slate-500 md:text-right">
          <p>Extraído em {formatDateTime(report.metadata.generated_at)}</p>
          {report.metadata.generated_by && <p>Usuário: {report.metadata.generated_by}</p>}
        </div>
      </div>
    </div>
  );
}

function ReportFooter({ report }: { report: ReportResult }) {
  return (
    <div className="mt-8 border-t pt-4 text-xs text-slate-500">
      <div className="flex flex-col justify-between gap-2 md:flex-row">
        <span>{report.metadata.powered_by}</span>
        <span>{report.metadata.confidentiality_text}</span>
      </div>
    </div>
  );
}

function EvolutionReportView({ report }: { report: PsychosocialEvolutionReport }) {
  return (
    <div className="space-y-6">
      {report.alerts?.map((alert) => (
        <div key={alert} className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {alert}
        </div>
      ))}

      <div className="grid gap-3 md:grid-cols-4">
        <StatCard label="Campanhas" value={report.summary.campaigns_count ?? 0} />
        <StatCard label="Dimensões" value={report.summary.dimensions_count ?? 0} />
        <StatCard label="Riscos" value={report.summary.risks_count ?? 0} />
        <StatCard label="Tendência geral" value={String(report.summary.general_trend_label || "-")} />
      </div>

      <section className="report-section rounded-2xl border bg-white p-4 shadow-sm">
        <div>
          <h3 className="text-base font-semibold text-slate-900">Linha do tempo das campanhas</h3>
          <p className="mt-1 text-sm text-slate-500">Campanhas ordenadas pelo mês/ano da data de abertura.</p>
        </div>
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">Campanha</th>
                <th className="px-3 py-2">Período</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Encerramento</th>
                <th className="px-3 py-2">Respondentes</th>
                <th className="px-3 py-2">Adesão</th>
              </tr>
            </thead>
            <tbody>
              {report.campaigns.map((campaign) => (
                <tr key={campaign.id} className="border-t">
                  <td className="px-3 py-2 font-medium">{campaign.title}</td>
                  <td className="px-3 py-2">{campaignLabel(campaign)}</td>
                  <td className="px-3 py-2">{statusLabel(campaign.status)}</td>
                  <td className="px-3 py-2">{formatDateTime(campaign.closed_at)}</td>
                  <td className="px-3 py-2">{campaign.total_responded ?? 0}/{campaign.total_invited ?? 0}</td>
                  <td className="px-3 py-2">{formatNumber(campaign.response_rate)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="report-section rounded-2xl border bg-white p-4 shadow-sm">
        <div className="flex flex-col justify-between gap-2 md:flex-row md:items-start">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Evolução por dimensão</h3>
            <p className="mt-1 text-sm text-slate-500">Média das dimensões ao longo de todas as campanhas selecionadas.</p>
          </div>
          <span className="rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">Escala 1 a 5</span>
        </div>
        <div className="mt-4">
          <EvolutionLineChart items={report.dimension_series} campaigns={report.campaigns} mode="dimension" />
          <EvolutionMatrixTable items={report.dimension_series} campaigns={report.campaigns} mode="dimension" />
        </div>
      </section>

      <section className="report-section rounded-2xl border bg-white p-4 shadow-sm">
        <div className="flex flex-col justify-between gap-2 md:flex-row md:items-start">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Evolução por risco</h3>
            <p className="mt-1 text-sm text-slate-500">Score do risco por campanha, calculado por probabilidade × severidade.</p>
          </div>
          <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700">Menor score = melhor</span>
        </div>
        <div className="mt-4">
          <EvolutionLineChart items={report.risk_series} campaigns={report.campaigns} mode="risk" />
          <EvolutionMatrixTable items={report.risk_series} campaigns={report.campaigns} mode="risk" />
        </div>
      </section>
    </div>
  );
}

function CampaignReportView({ report }: { report: CampaignAnalyticsReport }) {
  return (
    <div className="space-y-6">
      <div className="grid gap-3 md:grid-cols-4">
        <StatCard label="Participantes" value={report.summary.total_invited ?? 0} />
        <StatCard label="Respondentes" value={report.summary.total_responded ?? 0} />
        <StatCard label="Taxa de adesão" value={`${formatNumber(report.summary.response_rate)}%`} />
        <StatCard label="Dimensões" value={report.summary.dimensions_count ?? 0} />
      </div>

      <section className="report-section rounded-2xl border bg-white p-4 shadow-sm">
        <h3 className="text-base font-semibold text-slate-900">Resultado por dimensão</h3>
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">Dimensão</th>
                <th className="px-3 py-2">Média</th>
                <th className="px-3 py-2">Classificação</th>
                <th className="px-3 py-2">Respostas</th>
              </tr>
            </thead>
            <tbody>
              {report.dimensions.map((item) => (
                <tr key={item.dimension_id || item.dimension_name || Math.random()} className="border-t">
                  <td className="px-3 py-2 font-medium">{item.dimension_name}</td>
                  <td className="px-3 py-2">{formatNumber(item.average_score)}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex rounded-full border px-2 py-1 text-xs font-medium ${riskLevelClass(item.risk_level_code)}`}>
                      {item.risk_level_name}
                    </span>
                  </td>
                  <td className="px-3 py-2">{item.response_count ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="report-section rounded-2xl border bg-white p-4 shadow-sm">
        <h3 className="text-base font-semibold text-slate-900">Análise por pergunta</h3>
        <div className="mt-3 space-y-3">
          {report.questions.map((question: CampaignQuestionItem) => (
            <div key={question.question_id} className="rounded-xl border p-3">
              <div className="grid gap-3 md:grid-cols-[1fr_110px_1.2fr] md:items-center">
                <div>
                  <p className="text-xs text-slate-500">{question.dimension_name}</p>
                  <p className="text-sm font-medium text-slate-900">{question.statement}</p>
                </div>
                <div className="text-sm">
                  <p className="text-xs text-slate-500">Média</p>
                  <p className="font-semibold">{formatNumber(question.average_score)}</p>
                </div>
                <DistributionBar distribution={question.distribution} />
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function InventoryReportView({ report }: { report: RiskInventoryActionPlansReport }) {
  const riskDistribution = [
    { label: "Baixo", value: Number(report.summary.low || 0), className: "bg-emerald-500" },
    { label: "Moderado", value: Number(report.summary.moderate || 0), className: "bg-slate-400" },
    { label: "Alto", value: Number(report.summary.high || 0), className: "bg-amber-500" },
    { label: "Crítico", value: Number(report.summary.critical || 0), className: "bg-red-500" },
  ];
  const totalRisk = riskDistribution.reduce((sum, item) => sum + item.value, 0) || 1;

  return (
    <div className="space-y-6">
      <div className="grid gap-3 md:grid-cols-4">
        <StatCard label="Riscos no inventário" value={report.summary.total_risks ?? 0} />
        <StatCard label="Riscos sem plano" value={report.summary.risks_without_action_plan ?? 0} />
        <StatCard label="Total de ações" value={report.summary.actions_count ?? 0} />
        <StatCard label="Ações atrasadas" value={report.summary.overdue_actions ?? 0} />
        <StatCard label="Ações não iniciadas" value={report.summary.pending_actions ?? 0} />
        <StatCard label="Ações em andamento" value={report.summary.in_progress_actions ?? 0} />
        <StatCard label="Ações concluídas" value={report.summary.completed_actions ?? 0} />
        <StatCard label="Ações canceladas" value={report.summary.cancelled_actions ?? 0} />
      </div>

      <section className="report-section rounded-2xl border bg-white p-4 shadow-sm">
        <h3 className="text-base font-semibold text-slate-900">Riscos por classificação</h3>
        <div className="mt-4 space-y-3">
          {riskDistribution.map((item) => (
            <div key={item.label} className="grid grid-cols-[100px_1fr_50px] items-center gap-3 text-sm">
              <span className="text-slate-600">{item.label}</span>
              <div className="h-3 overflow-hidden rounded-full bg-slate-100">
                <div className={`print-color-exact h-full rounded-full ${item.className}`} style={{ width: `${(item.value / totalRisk) * 100}%` }} />
              </div>
              <span className="text-right font-semibold">{item.value}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="report-section rounded-2xl border bg-white p-4 shadow-sm">
        <h3 className="text-base font-semibold text-slate-900">Riscos do inventário</h3>
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-left text-xs md:text-sm">
            <thead className="text-[11px] uppercase text-slate-500">
              <tr>
                <th className="px-2 py-2">Risco</th>
                <th className="px-2 py-2">Dimensão</th>
                <th className="px-2 py-2">Classificação</th>
                <th className="px-2 py-2 text-right">Prob. média</th>
                <th className="px-2 py-2 text-right">Severidade</th>
                <th className="px-2 py-2 text-right">Score final</th>
                <th className="px-2 py-2 text-right">Planos</th>
                <th className="px-2 py-2 text-right">Ações</th>
                <th className="px-2 py-2 text-right">Atrasadas</th>
              </tr>
            </thead>
            <tbody>
              {report.risks.map((risk) => (
                <tr key={risk.inventory_item_id} className="border-t align-top">
                  <td className="max-w-[240px] px-2 py-2 font-medium">{risk.risk_title}</td>
                  <td className="max-w-[180px] px-2 py-2 text-slate-600">{risk.dimension_name || "-"}</td>
                  <td className="px-2 py-2">
                    <span className={`print-color-exact inline-flex rounded-full border px-2 py-1 text-[11px] font-medium ${riskLevelClass(risk.risk_level_code)}`}>
                      {risk.risk_level_name}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-right" title={risk.probability_name || undefined}>{formatNumber(risk.probability_score)}</td>
                  <td className="px-2 py-2 text-right" title={risk.severity_name || undefined}>{formatNumber(risk.severity_score)}</td>
                  <td className="px-2 py-2 text-right font-semibold">{formatNumber(risk.risk_score)}</td>
                  <td className="px-2 py-2 text-right">{risk.action_plans_count ?? 0}</td>
                  <td className="px-2 py-2 text-right">{risk.actions_count ?? 0}</td>
                  <td className="px-2 py-2 text-right">{risk.overdue_actions ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {report.risks_without_action_plan.length > 0 && (
        <section className="report-section rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <h3 className="text-base font-semibold text-amber-900">Riscos sem plano de ação</h3>
          <div className="mt-3 grid gap-2 md:grid-cols-2">
            {report.risks_without_action_plan.map((risk) => (
              <div key={risk.inventory_item_id} className="rounded-xl bg-white p-3 text-sm shadow-sm">
                <p className="font-medium text-slate-900">{risk.risk_title}</p>
                <p className="text-slate-500">{risk.dimension_name || "Sem dimensão informada"}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export default function ReportsPage() {
  const [reportType, setReportType] = useState<ReportType>("evolution");
  const [filters, setFilters] = useState<ReportFilters>(emptyFilters);
  const [report, setReport] = useState<ReportResult | null>(null);

  const optionsQ = useReportsOptions();
  const evolutionMut = useGeneratePsychosocialEvolutionReport();
  const campaignMut = useGenerateCampaignAnalyticsReport();
  const inventoryMut = useGenerateRiskInventoryActionPlansReport();
  const inventoryExcelMut = useDownloadRiskInventoryExcelReport();

  const isProcessing = optionsQ.isLoading || evolutionMut.isPending || campaignMut.isPending || inventoryMut.isPending || inventoryExcelMut.isPending;

  const options = optionsQ.data;
  const selectedInventoryCampaigns = useMemo(() => {
    if (!filters.inventory_id || !options?.campaigns) return [];
    return options.campaigns.filter((campaign) => campaign.default_inventory_id === filters.inventory_id);
  }, [filters.inventory_id, options?.campaigns]);

  function updateFilter(key: keyof ReportFilters, value: string) {
    setFilters((current) => ({ ...current, [key]: value }));
  }

  function validate() {
    if ((reportType === "evolution" || reportType === "inventory" || reportType === "inventory_export") && !filters.inventory_id) {
      toast.error(reportType === "inventory_export" ? "Selecione um inventário para exportar a planilha." : "Selecione um inventário para gerar este relatório.");
      return false;
    }

    if (reportType === "campaign" && !filters.campaign_id) {
      toast.error("Selecione uma campanha para gerar este relatório.");
      return false;
    }

    return true;
  }

  async function handleGenerate() {
    if (!validate()) return;

    if (reportType === "inventory_export") {
      await handleExportInventoryExcel();
      return;
    }

    try {
      if (reportType === "evolution") {
        const data = await evolutionMut.mutateAsync({
          inventory_id: filters.inventory_id,
          period_start: normalizeParam(filters.period_start),
          period_end: normalizeParam(filters.period_end),
          unit_id: normalizeParam(filters.unit_id),
          department_id: normalizeParam(filters.department_id),
          job_position_id: normalizeParam(filters.job_position_id),
          worker_group_id: normalizeParam(filters.worker_group_id),
          dimension_id: normalizeParam(filters.dimension_id),
          risk_id: normalizeParam(filters.risk_id),
        });
        setReport(data);
      }

      if (reportType === "campaign") {
        const data = await campaignMut.mutateAsync({
          campaign_id: filters.campaign_id,
          unit_id: normalizeParam(filters.unit_id),
          department_id: normalizeParam(filters.department_id),
          job_position_id: normalizeParam(filters.job_position_id),
          worker_group_id: normalizeParam(filters.worker_group_id),
          dimension_id: normalizeParam(filters.dimension_id),
          include_questions: true,
          include_distribution: true,
        });
        setReport(data);
      }

      if (reportType === "inventory") {
        const data = await inventoryMut.mutateAsync({
          inventory_id: filters.inventory_id,
          campaign_id: normalizeParam(filters.campaign_id),
          dimension_id: normalizeParam(filters.dimension_id),
          risk_id: normalizeParam(filters.risk_id),
          risk_classification: normalizeParam(filters.risk_classification),
          item_status: normalizeParam(filters.item_status),
          action_status: normalizeParam(filters.action_status),
          deadline_start: normalizeParam(filters.deadline_start),
          deadline_end: normalizeParam(filters.deadline_end),
        });
        setReport(data);
      }

      toast.success("Relatório gerado com sucesso.");
    } catch (error: any) {
      toast.error(error?.message || "Erro ao gerar relatório.");
    }
  }



  async function handleExportInventoryExcel() {
    if (!filters.inventory_id) {
      toast.error("Selecione um inventário para exportar a planilha.");
      return;
    }

    try {
      const file = await inventoryExcelMut.mutateAsync({
        inventory_id: filters.inventory_id,
        campaign_id: normalizeParam(filters.campaign_id),
        dimension_id: normalizeParam(filters.dimension_id),
        risk_id: normalizeParam(filters.risk_id),
        unit_id: normalizeParam(filters.unit_id),
        department_id: normalizeParam(filters.department_id),
        job_position_id: normalizeParam(filters.job_position_id),
        worker_group_id: normalizeParam(filters.worker_group_id),
        risk_classification: normalizeParam(filters.risk_classification),
        item_status: normalizeParam(filters.item_status),
        action_status: normalizeParam(filters.action_status),
        deadline_start: normalizeParam(filters.deadline_start),
        deadline_end: normalizeParam(filters.deadline_end),
      });
      saveDownload(file.blob, file.filename);
      toast.success("Exportação do inventário gerada com sucesso.");
    } catch (error: any) {
      toast.error(error?.message || "Erro ao exportar inventário em Excel.");
    }
  }

  function handlePrint() {
    if (!report) {
      toast.error("Gere um relatório antes de imprimir ou salvar em PDF.");
      return;
    }

    window.print();
  }

  return (
    <div className="space-y-6 pb-10">
      <ProcessingOverlay visible={isProcessing} />

      <style>
        {`
          .report-print-chart {
            display: none;
          }
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            @page {
              size: A4 landscape;
              margin: 10mm;
            }
            body * { visibility: hidden; }
            #report-print-area, #report-print-area * { visibility: visible; }
            #report-print-area {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              padding: 12px;
            }
            .no-print { display: none !important; }
            table { page-break-inside: auto; font-size: 11px; }
            tr { page-break-inside: avoid; page-break-after: auto; }
            section, .report-section {
              break-inside: avoid;
              page-break-inside: avoid;
              box-shadow: none !important;
            }
            .report-chart-frame {
              display: block !important;
              overflow: visible !important;
              padding: 8px !important;
              margin-bottom: 10px !important;
            }
            .report-screen-chart {
              display: none !important;
            }
            .report-print-chart {
              display: block !important;
              width: 100% !important;
              height: auto !important;
              overflow: visible !important;
            }
            .report-print-chart svg {
              display: block !important;
              width: 100% !important;
              height: auto !important;
              max-height: 70mm !important;
              overflow: visible !important;
            }
            .print-color-exact {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
          }
        `}
      </style>

      <div className="no-print">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-950">Relatórios NR-1</h1>
          <p className="mt-1 text-sm text-slate-600">
            Gere relatórios gerenciais com gráficos, indicadores, tabelas e opção de impressão/salvar como PDF.
          </p>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {reportCards.map((item) => {
            const Icon = item.icon;
            const active = reportType === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  setReportType(item.id);
                  setReport(null);
                }}
                className={`rounded-2xl border p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${
                  active ? "border-blue-300 bg-blue-50" : "border-slate-200 bg-white"
                }`}
              >
                <Icon className={`h-5 w-5 ${active ? "text-blue-700" : "text-slate-500"}`} />
                <h2 className="mt-3 font-semibold text-slate-950">{item.title}</h2>
                <p className="mt-1 text-sm text-slate-600">{item.description}</p>
              </button>
            );
          })}
        </div>

        <div className="mt-6 rounded-2xl border bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-950">Filtros do relatório</h2>
              <p className="text-sm text-slate-500">Os botões ficam bloqueados durante o processamento para evitar cliques repetidos.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={isProcessing}
                onClick={handleGenerate}
                className="inline-flex h-10 items-center gap-2 rounded-xl bg-blue-600 px-4 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isProcessing ? <Loader2 className="h-4 w-4 animate-spin" /> : reportType === "inventory_export" ? <Download className="h-4 w-4" /> : <BarChart3 className="h-4 w-4" />}
                {reportType === "inventory_export" ? "Exportar Excel" : "Gerar relatório"}
              </button>

              {reportType === "inventory" && (
                <button
                  type="button"
                  disabled={isProcessing || !filters.inventory_id}
                  onClick={handleExportInventoryExcel}
                  className="inline-flex h-10 items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 text-sm font-medium text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {inventoryExcelMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  Exportar Excel
                </button>
              )}
              {reportType !== "inventory_export" && (
                <button
                  type="button"
                  disabled={!report || isProcessing}
                  onClick={handlePrint}
                  className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Download className="h-4 w-4" />
                  Imprimir / salvar PDF
                </button>
              )}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {(reportType === "evolution" || reportType === "inventory" || reportType === "inventory_export") && (
              <SelectField
                label="Inventário"
                value={filters.inventory_id}
                onChange={(value) => updateFilter("inventory_id", value)}
                options={options?.inventories || []}
                placeholder="Selecione"
                required
              />
            )}

            {reportType === "campaign" && (
              <SelectField
                label="Campanha"
                value={filters.campaign_id}
                onChange={(value) => updateFilter("campaign_id", value)}
                options={options?.campaigns || []}
                placeholder="Selecione"
                required
              />
            )}

            {(reportType === "inventory" || reportType === "inventory_export") && (
              <SelectField
                label="Campanha de origem"
                value={filters.campaign_id}
                onChange={(value) => updateFilter("campaign_id", value)}
                options={selectedInventoryCampaigns.length ? selectedInventoryCampaigns : options?.campaigns || []}
              />
            )}

            <SelectField label="Unidade" value={filters.unit_id} onChange={(value) => updateFilter("unit_id", value)} options={options?.units || []} />
            <SelectField label="Departamento" value={filters.department_id} onChange={(value) => updateFilter("department_id", value)} options={options?.departments || []} />
            <SelectField label="Cargo" value={filters.job_position_id} onChange={(value) => updateFilter("job_position_id", value)} options={options?.job_positions || []} />
            <SelectField label="Grupo de avaliação" value={filters.worker_group_id} onChange={(value) => updateFilter("worker_group_id", value)} options={options?.worker_groups || []} />
            <SelectField label="Dimensão" value={filters.dimension_id} onChange={(value) => updateFilter("dimension_id", value)} options={options?.dimensions || []} />

            {(reportType === "evolution" || reportType === "inventory" || reportType === "inventory_export") && (
              <SelectField label="Risco" value={filters.risk_id} onChange={(value) => updateFilter("risk_id", value)} options={options?.risks || []} />
            )}

            {reportType === "evolution" && (
              <>
                <TextInput label="Período inicial" type="date" value={filters.period_start} onChange={(value) => updateFilter("period_start", value)} />
                <TextInput label="Período final" type="date" value={filters.period_end} onChange={(value) => updateFilter("period_end", value)} />
              </>
            )}

            {(reportType === "inventory" || reportType === "inventory_export") && (
              <>
                <SelectField
                  label="Classificação do risco"
                  value={filters.risk_classification}
                  onChange={(value) => updateFilter("risk_classification", value)}
                  placeholder="Todas"
                  options={[
                    { id: "low", title: "Baixo" },
                    { id: "moderate", title: "Moderado" },
                    { id: "high", title: "Alto" },
                    { id: "critical", title: "Crítico" },
                  ]}
                />
                <SelectField
                  label="Status da ação"
                  value={filters.action_status}
                  onChange={(value) => updateFilter("action_status", value)}
                  options={[
                    { id: "pending", title: "A Fazer" },
                    { id: "in_progress", title: "Em andamento" },
                    { id: "completed", title: "Concluído" },
                    { id: "cancelled", title: "Cancelado" },
                  ]}
                />
                <TextInput label="Prazo inicial" type="date" value={filters.deadline_start} onChange={(value) => updateFilter("deadline_start", value)} />
                <TextInput label="Prazo final" type="date" value={filters.deadline_end} onChange={(value) => updateFilter("deadline_end", value)} />
              </>
            )}
          </div>
        </div>
      </div>

      {report && (
        <div id="report-print-area" className="rounded-2xl border bg-white p-6 shadow-sm">
          <ReportHeader report={report} />

          <div className="mt-6">
            {reportType === "evolution" && <EvolutionReportView report={report as PsychosocialEvolutionReport} />}
            {reportType === "campaign" && <CampaignReportView report={report as CampaignAnalyticsReport} />}
            {reportType === "inventory" && <InventoryReportView report={report as RiskInventoryActionPlansReport} />}
          </div>

          <ReportFooter report={report} />
        </div>
      )}
    </div>
  );
}
